/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, CircularProgress, Container, Divider, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { Check, Pencil, X } from 'lucide-react';
import {
    bookingApiSlice,
    useGetBookingListQuery,
    useLazyGetBookingDetailsQuery,
} from '../../../api/booking/bookingApiSlice';
import {
    CancelSaveMode,
    useCancelSeriesBookingMutation,
    useDeleteBookingMutation,
} from '../../../api/booking/bookingWriteApiSlice';
import { CANCEL_MESSAGE_KEYS, cancelOutcomeMessage } from '../../../flows/cancelOutcome';
import { useCustomerMailHint } from '../../../hooks/useCustomerMailHint';
import StatusBadge, { BookingStatusType } from '../../../components/StatusBadge';
import CustomButton from '../../../components/CustomButton';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import CustomModal from '../../common/CustomModal';
import SnackbarComponent from '../../common/SnackbarComponent';
import TextFieldComponent from '../../common/TextFieldComponent';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { makeRentalBookingDetailsUrl, paths } from '../../../routes';
import { formatCurrency, isValidEmail } from '../../../utils';
import { getCurrentLang } from '../../../i18n/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
    setConfirmationDialogContent,
    setIsConfirmationDialog,
} from '../../../store/slices/globalSlice';
import { PaymentStatusType } from '../../../api/booking/bookingApiSlice';
import { useIsMaster } from '../../../common/useIsMaster';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import {
    RentalBookingDetails,
    SERIES_PAYMENT_TERM_SELECTIONS,
    SeriesPaymentTermSelection,
    selectionToSeriesPaymentTerm,
    seriesPaymentTermToSelection,
} from '../../../types/bookingCreation';
import { BookingDetailsAdditionalService } from '../../../store/slices/bookingCreationSlice';
import { useEditSeriesBooking } from '../../../flows/editSeriesBookingFlow';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { attributionIfRead, attributionPayloadForEdit } from '../../../utils/costCenter';
import { useGetSeriesBookingQuery } from '../../../api/booking/seriesBookingApiSlice';
import BookingEditDetailsAdditionalServices from '../EditBooking/components/AdditionalServices/BookingEditDetailsAdditionalServices';
import SeriesExtendPanel from './SeriesExtendPanel';
import SeriesShortenPanel from './SeriesShortenPanel';
import { Booking } from './types';
import { deleteBookingErrorMessage } from '../../../utils/deleteBookingErrorMessage';
import { CoveredBy, paymentBadgeStatus, seriesCoveredBy } from '../../../utils/bookingCoverage';
import CoveredAmountNote from '../../../components/reservationSummary/CoveredAmountNote';
import BookingCountryField from '../../../components/booking/BookingCountryField';
import { roomNounOf } from '../../../utils/useCaseLabels';
import { savedOutcomeToast } from '../../../utils/customerMailGuard';


/** "Fälliger Betrag", or on a covered series who bears it (ADR-0008 2026-09-23). */
const SeriesAmountDue = ({ coveredBy, amountDue }: Readonly<{ coveredBy: CoveredBy | null, amountDue: number }>) => {
    const { t } = useTranslation();
    if (coveredBy) return <CoveredAmountNote coveredBy={coveredBy} />;
    return (
        <div className={'flex justify-between text-sm'}>
            <span>{t('booking_details.reservation_summary.amount_due')}</span>
            <span className={'font-bold'}>{formatCurrency(amountDue, getCurrentLang())}</span>
        </div>
    );
};

const SeriesBookingDetailsPage: React.FC = () => {
    const { seriesId } = useParams<{ seriesId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';

    const loadingGlobal = useSelector((state: RootState) => state.global.isLoading);
    const [cancelSeriesBooking] = useCancelSeriesBookingMutation();
    const fetchMailHint = useCustomerMailHint();
    // Bumped every time the confirm dialog is opened, so a late-arriving
    // mail hint can tell whether it is still the dialog on screen.
    const dialogRequestRef = React.useRef(0);
    const [deleteBooking] = useDeleteBookingMutation();
    // Deleting a booking is a master-only action — hidden from tenant owners
    // even though they hold the deleteBookings permission.
    const isMaster = useIsMaster();
    // Cancel + edit require edit:bookings (Service holds only booking VIEW).
    const hasEditBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.editBookings()),
    );
    const [dialogAction, setDialogAction] = useState<'cancel' | 'delete' | null>(null);
    // This page never dispatches `setIsLoading`, so the confirm dialog's own
    // disable/spinner machinery — which reads the GLOBAL flag — was inert here:
    // both commits stayed live for the whole request. Tracked locally instead,
    // the way SeriesShortenPanel already does it.
    const [isCancelingSeries, setIsCancelingSeries] = useState(false);

    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });

    // Editable series-level state. Payment term uses the selector model
    // introduced in PRs #974-#980 (three terms: monthly instalments /
    // lump-sum upfront / lump-sum at end). Phase 2 (#771) also covers
    // customer data, an absolute-EUR series discount, and add-ons applied
    // per occurrence. Seeded via the init-guard effect below.
    const [isEditing, setIsEditing] = useState(false);
    // Two independent seed guards so the customer/add-on card (from the
    // booking details) renders even if the series-metadata GET is delayed
    // or failing, and vice versa.
    const [didSeedCustomer, setDidSeedCustomer] = useState(false);
    const [didSeedMeta, setDidSeedMeta] = useState(false);
    const [loadingSaveOnly, setLoadingSaveOnly] = useState(false);
    const [loadingSaveAndSend, setLoadingSaveAndSend] = useState(false);
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);

    // Current editable values + their snapshot at enter-edit-time (for Discard).
    const emptyCustomer = useMemo(() => ({
        firstName: '', lastName: '', email: '', phone: '',
        address: '', country: '', city: '', zipCode: '', company: '',
    }), []);
    const [paymentTerm, setPaymentTerm] = useState<SeriesPaymentTermSelection>('instalments_monthly');
    const [editableCustomer, setEditableCustomer] = useState(emptyCustomer);
    const [seriesDiscount, setSeriesDiscount] = useState<number>(0);
    const [editableServices, setEditableServices] = useState<BookingDetailsAdditionalService[]>([]);

    const [initialPaymentTerm, setInitialPaymentTerm] = useState<SeriesPaymentTermSelection>('instalments_monthly');
    const [initialCustomer, setInitialCustomer] = useState(emptyCustomer);
    // #1810: organisation (unit) and cost centre, the same for the whole series.
    const [editableAttribution, setEditableAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    const [initialAttribution, setInitialAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    const [initialSeriesDiscount, setInitialSeriesDiscount] = useState<number>(0);
    const [initialServices, setInitialServices] = useState<BookingDetailsAdditionalService[]>([]);

    // Inline edit state for the series-discount row in the price summary.
    const [editingSeriesDiscount, setEditingSeriesDiscount] = useState(false);
    const [tempSeriesDiscount, setTempSeriesDiscount] = useState('');

    // Which occurrence-count action is open (#1376 item 1). Mutually exclusive:
    // extending and shortening both rewrite the occurrence list, so only one
    // panel is ever mounted.
    const [occurrenceAction, setOccurrenceAction] = useState<'none' | 'extend' | 'shorten'>('none');

    const editSeriesBooking = useEditSeriesBooking(seriesId ?? '');

    const { data: seriesDetails } = useGetSeriesBookingQuery(seriesId ?? '', {
        skip: !seriesId,
    });

    // Fetch all bookings, filter by seriesId
    const { data: allBookings = [], isLoading: isListLoading } = useGetBookingListQuery('apartment,venue');

    const seriesBookings = useMemo(() => {
        if (!seriesId || !allBookings.length) return [];
        return (allBookings as Booking[])
            .filter((b) => b.seriesId === seriesId)
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    }, [allBookings, seriesId]);

    // Fetch full details for all bookings (for customer info + aggregated pricing)
    const [allDetails, setAllDetails] = useState<RentalBookingDetails[]>([]);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [triggerDetail] = useLazyGetBookingDetailsQuery();

    useEffect(() => {
        if (seriesBookings.length === 0) return;
        setIsLoadingDetails(true);
        Promise.all(
            seriesBookings.map((b) => triggerDetail({ orderId: b.bookingId }).unwrap()),
        ).then((results) => {
            setAllDetails(results);
            setIsLoadingDetails(false);
        }).catch(() => {
            setIsLoadingDetails(false);
        });
    }, [seriesBookings]);

    const firstBookingDetails = allDetails[0] ?? null;

    // The details per booking, for the payment badge: coverage and free by
    // price (PO, 2026-10-23) come with the details, not with the list row.
    const detailsById = useMemo(
        () => new Map(allDetails.map((d) => [d.id, d])),
        [allDetails],
    );

    // Seed the editable fields once, from two independent sources. They are
    // seeded separately (not gated on both arriving) so a slow or failing
    // series-metadata GET never blanks the customer card / add-on list, which
    // come from the booking details and not from the series endpoint:
    // - customer + services: from the first booking; the same customer /
    //   add-on set applies to the whole series (see #771 semantics).
    // - paymentTerm + seriesDiscount: from GET /order/series/:seriesId. The
    //   structured four-field term (quarterly / custom cadences collapse to
    //   instalments_monthly until surfaced) + the #771 series discount (EUR,
    //   on top of per-booking discounts, defaults to 0).
    // #1810: the attribution seeds only from a read that SUCCEEDED, in an
    // effect of its own, so a failed first read followed by a good refetch
    // seeds it then — and the save sends attribution only once it was read.
    const attributionSeeded = React.useRef(false);
    useEffect(() => {
        if (attributionSeeded.current || firstBookingDetails?.attributionStatus !== 'ok') return;
        attributionSeeded.current = true;
        const attribution: BookingAttribution = {
            organizationId: firstBookingDetails.organizationId ?? null,
            organizationUnitId: firstBookingDetails.organizationUnitId ?? null,
            costCenter: firstBookingDetails.costCenter ?? '',
        };
        setEditableAttribution(attribution);
        setInitialAttribution(attribution);
    }, [firstBookingDetails]);

    useEffect(() => {
        if (didSeedCustomer || !firstBookingDetails) return;

        const cust = {
            firstName: firstBookingDetails.customerDetails?.firstName ?? '',
            lastName: firstBookingDetails.customerDetails?.lastName ?? '',
            email: firstBookingDetails.customerDetails?.email ?? '',
            phone: firstBookingDetails.customerDetails?.phone ?? '',
            address: firstBookingDetails.customerDetails?.address ?? '',
            country: firstBookingDetails.customerDetails?.country ?? '',
            city: firstBookingDetails.customerDetails?.city ?? '',
            zipCode: firstBookingDetails.customerDetails?.zipCode ?? '',
            company: firstBookingDetails.customerDetails?.company ?? '',
        };
        const svcs = firstBookingDetails.additionalService ?? [];

        setEditableCustomer(cust);
        setInitialCustomer(cust);
        setEditableServices(svcs);
        setInitialServices(svcs);
        setDidSeedCustomer(true);
    }, [firstBookingDetails, didSeedCustomer]);

    useEffect(() => {
        if (didSeedMeta || !seriesDetails) return;

        const seededTerm = seriesPaymentTermToSelection(seriesDetails);
        const disc = seriesDetails.discount ?? 0;

        setPaymentTerm(seededTerm);
        setInitialPaymentTerm(seededTerm);
        setSeriesDiscount(disc);
        setInitialSeriesDiscount(disc);
        setDidSeedMeta(true);
    }, [seriesDetails, didSeedMeta]);

    // Aggregated pricing
    const aggregatedPricing = useMemo(() => {
        if (allDetails.length === 0) return null;
        const total = allDetails.reduce((sum, d) => sum + (d.payment?.total ?? 0), 0);
        const amountPaid = allDetails.reduce((sum, d) => sum + (d.payment?.amountPaid ?? 0), 0);
        const discount = allDetails.reduce((sum, d) => sum + (d.payment?.couponApplied ?? 0), 0);
        return { total, amountPaid, discount, balance: total - amountPaid - discount, coveredBy: seriesCoveredBy(allDetails) };
    }, [allDetails]);

    // Booking number range
    const bookingNumberRange = useMemo(() => {
        if (seriesBookings.length === 0) return '';
        const numbers = seriesBookings.map((b) => b.bookingNumber).sort((a, b) => a.localeCompare(b));
        const first = numbers[0];
        const last = numbers[numbers.length - 1];
        if (numbers.length === 1) return first;
        let prefixLen = 0;
        while (prefixLen < first.length && first[prefixLen] === last[prefixLen]) {
            prefixLen++;
        }
        return prefixLen > 0 ? `${first}\u2013${last.slice(prefixLen)}` : `${first}\u2013${last}`;
    }, [seriesBookings]);

    // Status summary
    const statusSummary = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const b of seriesBookings) {
            const status = b.bookingStatus.toLowerCase();
            counts[status] = (counts[status] ?? 0) + 1;
        }
        return Object.entries(counts).sort(([, a], [, b]) => b - a);
    }, [seriesBookings]);

    // Bookings eligible for the confirmation email: created + conflict-free.
    // Excluded: canceled or conflicted. See issue #770 "Mail Logic" table.
    const eligibleBookingCount = useMemo(() => {
        return seriesBookings.filter((b) => {
            const s = b.bookingStatus.toLowerCase();
            return s !== 'canceled' && s !== 'conflicted';
        }).length;
    }, [seriesBookings]);

    // Everything the extend panel needs to create a further occurrence, read
    // off the series' existing bookings (#1376 item 1). The occurrences share
    // asset, time window and customer, so the first one defines them; the last
    // one defines where the series currently ends.
    //
    // Times come from the booking list (plain ISO strings) rather than from the
    // details' `checkIn`/`checkOut`, which are the same values in a shape that
    // varies between endpoints. `null` while the details are still loading —
    // the panel is simply not offered until the data it needs is there.
    const extendSource = useMemo(() => {
        const first = seriesBookings[0];
        const last = seriesBookings[seriesBookings.length - 1];
        // The series routes book the OFFER (#2650): no offer, nothing to extend.
        if (!first || !last || !firstBookingDetails?.groupId) return null;

        return {
            groupId: firstBookingDetails.groupId,
            lastOccurrenceDate: dayjs(last.startDate),
            startTime: dayjs(first.startDate).format('HH:mm'),
            endTime: dayjs(first.endDate).format('HH:mm'),
            numberOfGuests: firstBookingDetails.numberOfGuests,
            eventType: firstBookingDetails.eventType,
            comment: firstBookingDetails.comment,
        };
    }, [seriesBookings, firstBookingDetails]);

    // Payment summary
    const paymentSummary = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const b of seriesBookings) {
            const status = b.paymentStatus?.toLowerCase() ?? 'unpaid';
            const normalized = status === 'success' || status === 'paid' ? 'paid' : status === 'open' ? 'unpaid' : status;
            const badge = paymentBadgeStatus(detailsById.get(b.bookingId), normalized);
            counts[badge] = (counts[badge] ?? 0) + 1;
        }
        return Object.entries(counts).sort(([, a], [, b]) => b - a);
    }, [seriesBookings, detailsById]);

    // Cancel series
    const handleClickCancelSeries = async () => {
        setDialogAction('cancel');
        // Content BEFORE opening, never after: the dialog renders from the
        // store, so opening first would show whatever the last dialog left
        // there — blank on the first open, or the DELETE text and buttons on a
        // page where delete was opened once.
        const base = {
            content: t('series_booking_details.cancel_dialog.content', {
                count: seriesBookings.length,
            }),
            headerText: t('series_booking_details.cancel_dialog.title'),
            leftButtonText: t('common.cancel'),
            middleButtonText: t('series_booking_details.cancel_dialog.confirm_without_email'),
            rightButtonText: t('series_booking_details.cancel_dialog.confirm'),
        };
        dispatch(setConfirmationDialogContent(base));
        dispatch(setIsConfirmationDialog(true));

        // Any occurrence answers it: a series mail is ONE mail covering every
        // date, and `recordSeriesMailOutcome` writes an event on each covered
        // booking precisely so a per-order read can see it. Fetched after the
        // dialog is up so a slow read cannot make the button read as dead.
        const request = ++dialogRequestRef.current;
        const mailHint = await fetchMailHint(seriesBookings[0]?.bookingId ?? '');
        if (!mailHint || request !== dialogRequestRef.current) return;
        dispatch(
            setConfirmationDialogContent({
                ...base,
                content: `${base.content} ${mailHint}`,
            }),
        );
    };

    const handleCancelSeries = async (saveMode: CancelSaveMode) => {
        if (isCancelingSeries) return;
        // Without an id the request would POST to `/order/series//cancel`.
        // Refusing here is the honest outcome — the alternative is a 404 the
        // operator reads as "the cancellation failed" on a page that should
        // never have offered the button.
        if (!seriesId) return;
        setIsCancelingSeries(true);
        try {
            // ONE request, ONE mail. This used to loop the per-booking cancel
            // endpoint, so a 20-date series sent the customer twenty
            // near-identical notices — the opposite of how the same customer
            // was told the series existed (`sendSeriesEmail` has always sent a
            // single confirmation listing every date). The endpoint also
            // tolerates occurrences already cancelled individually, which is
            // what the 409-swallowing loop here was doing by hand.
            const result = await cancelSeriesBooking({
                seriesId,
                saveMode,
                // Said out loud: the endpoint will not treat an unreadable
                // body as "cancel every occurrence".
                scope: 'all',
            }).unwrap();
            // Cancel lives on the new-backend write slice (no tagTypes) —
            // invalidate the legacy slice's caches by hand.
            dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
            // Nothing cancelled means nothing to mail about, so the absence of
            // a mail is not a failure — reporting "die E-Mail konnte nicht
            // gesendet werden" for a run that cancelled nothing describes a
            // problem that does not exist.
            const nothingToDo = (result?.canceled ?? 0) === 0;
            const outcome = nothingToDo
                ? cancelOutcomeMessage('save_only', false, CANCEL_MESSAGE_KEYS.series)
                : cancelOutcomeMessage(saveMode, result?.mailSent === true, CANCEL_MESSAGE_KEYS.series);
            // The endpoint finishes its run even when an occurrence throws, so
            // "some of the series is still booked" is a real outcome. Saying
            // "alle Buchungen der Serie wurden storniert" over it would be the
            // kind of reported success this codebase keeps getting bitten by —
            // and the page would then navigate away from the evidence.
            const someFailed = (result?.failed ?? 0) > 0;
            setSnackbarData({
                open: true,
                message: someFailed
                    ? t('series_booking_details.cancel_partial', {
                        canceled: result?.canceled ?? 0,
                        failed: result?.failed ?? 0,
                    })
                    : t(outcome.messageKey),
                severity: someFailed ? 'warning' : outcome.severity,
            });
            // Only leave the page when there is nothing left to look at.
            if (!someFailed) {
                setTimeout(() => navigate(paths.rentalBooking), 1500);
            }
        } catch {
            setSnackbarData({
                open: true,
                message: t('series_booking_details.cancel_error'),
                severity: 'error',
            });
        } finally {
            // On every outcome: the dialog used to sit open over the page and
            // its error snackbar, so a failed series cancel looked like a click
            // that did nothing.
            setIsCancelingSeries(false);
            dispatch(setIsConfirmationDialog(false));
        }
    };

    // Delete series
    const handleClickDeleteSeries = () => {
        setDialogAction('delete');
        // Invalidates any cancel-dialog mail hint still in flight, so it
        // cannot land on top of this dialog's text.
        dialogRequestRef.current += 1;
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                content: t('series_booking_details.delete_dialog.content', {
                    count: seriesBookings.length,
                }),
                headerText: t('series_booking_details.delete_dialog.title'),
                leftButtonText: t('common.cancel'),
                rightButtonText: t('series_booking_details.delete_dialog.confirm'),
            }),
        );
    };

    const handleDeleteSeries = async () => {
        try {
            for (const booking of seriesBookings) {
                await deleteBooking(booking.bookingId).unwrap();
            }
            dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
            setSnackbarData({
                open: true,
                message: t('series_booking_details.delete_success'),
                severity: 'success',
            });
            setTimeout(() => navigate(paths.rentalBooking), 1500);
        } catch (error) {
            setSnackbarData({
                open: true,
                message: deleteBookingErrorMessage(error, t, 'series_booking_details.delete_error'),
                severity: 'error',
            });
        }
    };

    // The create loop invalidates `bookingList`, so the occurrence list and the
    // derived count refresh on their own — these only report the outcome.
    const handleExtended = (created: number, failed: number) => {
        setSnackbarData({
            open: true,
            message: failed > 0
                ? t('series_booking_details.extend.snackbar_partial', { created, failed })
                : t('series_booking_details.extend.snackbar_success', { count: created }),
            severity: failed > 0 ? 'warning' : 'success',
        });
    };

    const handleShortened = (
        canceled: number,
        failed: number,
        mailFailed: boolean,
        outcomeUnknown?: boolean,
    ) => {
        setOccurrenceAction('none');
        if (outcomeUnknown) {
            // The request never came back, but the server may have finished —
            // and mailed the customer. Reporting a failure here is what sends
            // an operator to cancel the same dates a second time. The list
            // below is refetched either way, so the screen shows the truth.
            setSnackbarData({
                open: true,
                message: t('series_booking_details.shorten.snackbar_unknown'),
                severity: 'warning',
            });
            return;
        }
        const base = failed > 0
            ? t('series_booking_details.shorten.snackbar_partial', { canceled, failed })
            : t('series_booking_details.shorten.snackbar_success', { count: canceled });
        // A requested-but-undelivered mail is appended rather than replacing
        // the count: both facts matter, and the occurrences really were
        // cancelled.
        const message = mailFailed
            ? `${base} ${t('series_booking_details.shorten.snackbar_mail_failed')}`
            : base;
        setSnackbarData({
            open: true,
            message,
            severity: failed > 0 || mailFailed ? 'warning' : 'success',
        });
    };

    const handleEnterEdit = () => {
        setInitialPaymentTerm(paymentTerm);
        setInitialCustomer(editableCustomer);
        setInitialSeriesDiscount(seriesDiscount);
        setInitialServices(editableServices);
        setIsEditing(true);
    };

    const handleDiscardEdit = () => {
        setPaymentTerm(initialPaymentTerm);
        setEditableCustomer(initialCustomer);
        setEditableAttribution(initialAttribution);
        setSeriesDiscount(initialSeriesDiscount);
        setEditableServices(initialServices);
        setEditingSeriesDiscount(false);
        setTempSeriesDiscount('');
        setIsEditing(false);
    };

    // Snackbar copy stays neutral ("saved") because the email-send path
    // cannot confirm delivery synchronously.
    const handleSaveSeries = async (saveMode: 'save_only' | 'save_and_send_email') => {
        if (!seriesId) return;
        if (saveMode === 'save_only') setLoadingSaveOnly(true);
        else setLoadingSaveAndSend(true);

        const term = selectionToSeriesPaymentTerm(paymentTerm);
        const { success, mailBlocked } = await editSeriesBooking(
            {
                ...term,
                customerDetails: editableCustomer,
                ...attributionPayloadForEdit(attributionSeeded.current ? 'ok' : undefined, editableAttribution),
                discount: seriesDiscount,
                additionalServices: editableServices.map((svc) => ({
                    serviceId: svc.id,
                    name: svc.serviceName,
                    quantity: svc.quantity ?? 1,
                })),
            },
            saveMode,
        );

        if (success) {
            setInitialPaymentTerm(paymentTerm);
            setInitialCustomer(editableCustomer);
            setInitialSeriesDiscount(seriesDiscount);
            setInitialServices(editableServices);
            setInitialAttribution(editableAttribution);
            setEditingSeriesDiscount(false);
            setIsEditing(false);
            setSnackbarData({
                open: true,
                // Saved — but a refused mail is said, never shown as a plain success.
                ...savedOutcomeToast(t, mailBlocked, t('series_booking_details.save_success')),
            });
        } else {
            setSnackbarData({
                open: true,
                message: t('series_booking_details.save_error'),
                severity: 'error',
            });
        }

        setLoadingSaveOnly(false);
        setLoadingSaveAndSend(false);
        setConfirmSendOpen(false);
    };

    // Customer email must be a valid address before saving — a series confirmation
    // mail is worthless if the recipient field is garbage. Validation is based on
    // form state (editableCustomer), not the API response (CLAUDE rule #4).
    const isCustomerEmailValid = isValidEmail(editableCustomer.email);

    // Block Save while the inline discount input is open and uncommitted —
    // otherwise the admin's typed value would be silently dropped on Save.
    // Also block when the email is invalid while editing.
    const saveButtonsDisabled =
        loadingSaveOnly
        || loadingSaveAndSend
        || editingSeriesDiscount
        || (isEditing && !isCustomerEmailValid);

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: paths.rentalBooking },
        { label: bookingNumberRange, isActive: true },
    ];

    const firstBooking = seriesBookings[0];
    const lastBooking = seriesBookings[seriesBookings.length - 1];

    const isLoading = isListLoading || isLoadingDetails;

    if (isLoading && !firstBookingDetails) {
        return (
            <Container maxWidth={false} className={'px-0 sm:px-4'}>
                <Box sx={{ py: 3 }}>
                    <div className={'flex h-[80vh] w-full items-center justify-center'}>
                        <CircularProgress />
                    </div>
                </Box>
            </Container>
        );
    }

    if (!seriesId || seriesBookings.length === 0) {
        return (
            <Container maxWidth={false} className={'px-0 sm:px-4'}>
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant={'h5'} color={'text.primary'} fontWeight={600}>
                        {t('booking_details.not_found.title')}
                    </Typography>
                    <Typography variant={'body1'} color={'text.secondary'} sx={{ mt: 1 }}>
                        {t('booking_details.not_found.message')}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <CustomButton
                            title={t('booking_details.not_found.back_to_bookings')}
                            onClick={() => navigate(paths.rentalBooking)}
                            isFilled
                        />
                    </Box>
                </Box>
            </Container>
        );
    }

    const formatDateDisplay = (dateStr: string) => {
        return dayjs(dateStr).locale(lang).format(lang === 'de' ? 'DD.MM.YYYY' : 'MM/DD/YYYY');
    };

    const getPaymentStatusLabel = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'success' || s === 'paid') return 'paid';
        if (s === 'open') return 'unpaid';
        return status;
    };

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <PageHeadline>
                        {t('series_booking_details.title')}
                    </PageHeadline>
                </PageHeader>
                <div className={'mb-6'}>
                    <CustomSeparator items={breadcrumbItems} />
                </div>
                <Paper sx={{ padding: '20px' }}>
                    {/* Header */}
                    <div className={'mb-4 flex justify-between bg-[#F5F5F5] p-3'}>
                        <div className={'flex w-full flex-col'}>
                            <div className={'flex flex-col justify-between xl:flex-row'}>
                                <div className={'mb-2 flex items-center'}>
                                    <div className={'flex flex-col xl:flex-row'}>
                                        <h1 className={'mr-4 text-2xl font-medium'}>{bookingNumberRange}</h1>
                                        <div className={'my-2 flex flex-row flex-wrap gap-2 xl:my-0'}>
                                            {statusSummary.map(([status, count]) => (
                                                <div key={`s-${status}`} className={'flex items-center gap-1'}>
                                                    <StatusBadge
                                                        type={status as BookingStatusType}
                                                        isEditing={false}
                                                    />
                                                    <span className={'text-sm font-medium text-gray-700'}>{count}</span>
                                                </div>
                                            ))}
                                            {paymentSummary.map(([status, count]) => (
                                                <div key={`p-${status}`} className={'flex items-center gap-1'}>
                                                    <StatusBadge
                                                        type={status as PaymentStatusType}
                                                        isEditing={false}
                                                    />
                                                    <span className={'text-sm font-medium text-gray-700'}>{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className={'mb-2 flex space-x-2'}>
                                    {isMaster && (
                                        <CustomButton
                                            title={t('booking_details.buttons.delete_booking')}
                                            onClick={handleClickDeleteSeries}
                                            isFilled={false}
                                            variant={'delete'}
                                        />
                                    )}
                                    {hasEditBookingsPermission && (
                                        <CustomButton
                                            title={t('booking_details.buttons.cancel_booking')}
                                            onClick={handleClickCancelSeries}
                                            isFilled={false}
                                        />
                                    )}
                                    {!isEditing && hasEditBookingsPermission && (
                                        <CustomButton
                                            title={t('booking_details.buttons.edit_button')}
                                            onClick={handleEnterEdit}
                                            isFilled={true}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={'flex items-start gap-4 text-sm text-gray-500'}>
                                <div>
                                    <p>
                                        {t('series_booking_details.booking_count')}: <span className={'font-bold'}>{seriesBookings.length}</span>
                                    </p>
                                    {firstBookingDetails?.created_at && (
                                        <p>
                                            {t('booking_details.created_on')}: <span className={'font-bold'}>{formatDateDisplay(firstBookingDetails.created_at)}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Reservation Details */}
                    <Typography variant={'h3'} className={'mb-10 mt-5'}>
                        {t('create_booking.reservation_details.title')}
                    </Typography>
                    <Grid container spacing={2} sx={{ width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex', ml: '0px' }}>
                        {/* Price Summary - Right */}
                        <Grid container spacing={2} className={'order-1 mb-8 mt-1 flex w-full items-end justify-end xl:order-2 xl:mb-0 xl:mt-0 xl:w-[30%]'}>
                            <div className={'col-span-12 mx-auto min-h-[32px] w-[90%] rounded border border-[#DEDFDF] px-3 py-4 md:col-span-4 xl:ml-auto xl:mr-0 xl:w-[70%]'}>
                                <h3 className={'mb-3 text-sm font-semibold text-black'}>{t('create_booking.reservation_details.title')}</h3>
                                <div className={'space-y-2'}>
                                    <div className={'flex justify-between text-sm'}>
                                        <span>{t('series_booking_details.first_date')}:</span>
                                        <span>{firstBooking ? formatDateDisplay(firstBooking.startDate) : ''}</span>
                                    </div>
                                    <div className={'flex justify-between text-sm'}>
                                        <span>{t('series_booking_details.last_date')}:</span>
                                        <span>{lastBooking ? formatDateDisplay(lastBooking.endDate) : ''}</span>
                                    </div>
                                    <div className={'flex justify-between text-sm'}>
                                        <span>{t('series_booking_details.booking_count')}:</span>
                                        <span>{seriesBookings.length}</span>
                                    </div>
                                </div>

                                <div className={'my-2 border-b border-gray-300'}></div>

                                <h3 className={'mb-3 text-sm font-semibold text-black'}>{t('booking_details.reservation_summary.price')}</h3>
                                {isLoadingDetails ? (
                                    <div className={'flex justify-center py-2'}>
                                        <CircularProgress size={20} />
                                    </div>
                                ) : aggregatedPricing && (
                                    <div className={'space-y-2'}>
                                        <div className={'flex justify-between text-sm'}>
                                            <span>{t('series_booking_details.price_summary.total_individual_bookings')}</span>
                                            <span>{formatCurrency(aggregatedPricing.total, getCurrentLang())}</span>
                                        </div>
                                        {aggregatedPricing.discount > 0 && (
                                            <div className={'flex justify-between text-sm'}>
                                                <span>{t('booking_details.reservation_summary.coupon_applied')}</span>
                                                <span>-{formatCurrency(aggregatedPricing.discount, getCurrentLang())}</span>
                                            </div>
                                        )}

                                        {/* Series-level discount — inline pencil-edit (Phase 2, #771) */}
                                        <div className={'flex justify-between items-center text-sm min-h-[32px]'}>
                                            <span>{t('series_booking_details.price_summary.series_discount')}</span>
                                            <div className={'flex items-center gap-2'}>
                                                {isEditing && editingSeriesDiscount ? (
                                                    <>
                                                        <input
                                                            type={'number'}
                                                            className={'w-20 px-2 py-0.5 border rounded text-right text-sm h-[28px] appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'}
                                                            value={tempSeriesDiscount}
                                                            min={0}
                                                            max={aggregatedPricing.total}
                                                            onChange={(e) => setTempSeriesDiscount(e.target.value)}
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                const v = parseFloat(tempSeriesDiscount);
                                                                const clamped = Number.isNaN(v)
                                                                    ? 0
                                                                    : Math.min(aggregatedPricing.total, Math.max(0, v));
                                                                setSeriesDiscount(clamped);
                                                                setEditingSeriesDiscount(false);
                                                            }}
                                                            className={'text-green-600 hover:text-green-800 h-[28px] flex items-center'}
                                                            aria-label={'Confirm'}
                                                        >
                                                            <Check size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setTempSeriesDiscount(String(seriesDiscount));
                                                                setEditingSeriesDiscount(false);
                                                            }}
                                                            className={'text-red-600 hover:text-red-800 h-[28px] flex items-center'}
                                                            aria-label={'Cancel'}
                                                        >
                                                            <X size={18} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>
                                                            {seriesDiscount > 0 ? '-' : ''}
                                                            {formatCurrency(seriesDiscount, getCurrentLang())}
                                                        </span>
                                                        {isEditing && (
                                                            <button
                                                                onClick={() => {
                                                                    setTempSeriesDiscount(String(seriesDiscount));
                                                                    setEditingSeriesDiscount(true);
                                                                }}
                                                                className={'text-gray-500 hover:text-black h-[28px] flex items-center'}
                                                                aria-label={'Edit series discount'}
                                                            >
                                                                <Pencil size={16} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {seriesDiscount > 0 && aggregatedPricing.total > 0 && (
                                            <div className={'flex justify-end text-xs text-gray-500 -mt-1'}>
                                                ≈ {((seriesDiscount / aggregatedPricing.total) * 100).toFixed(1)}%
                                            </div>
                                        )}

                                        <div className={'my-2 border-b border-gray-300'}></div>

                                        <div className={'flex justify-between text-sm'}>
                                            <span className={'font-semibold'}>{t('series_booking_details.price_summary.total_series_booking')}</span>
                                            <span className={'font-bold'}>{formatCurrency(aggregatedPricing.total - seriesDiscount, getCurrentLang())}</span>
                                        </div>
                                        <div className={'flex justify-between text-sm'}>
                                            <span>{t('booking_details.reservation_summary.already_paid')}</span>
                                            <span>{formatCurrency(aggregatedPricing.amountPaid, getCurrentLang())}</span>
                                        </div>
                                        <SeriesAmountDue
                                            coveredBy={aggregatedPricing.coveredBy}
                                            amountDue={aggregatedPricing.balance - seriesDiscount}
                                        />
                                    </div>
                                )}

                            </div>
                        </Grid>

                        {/* Form Fields - Left */}
                        <Grid container spacing={2} className={'order-2 flex w-full items-start justify-start xl:order-1 xl:w-[70%]'}>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled
                                    value={firstBooking ? formatDateDisplay(firstBooking.startDate) : ''}
                                    fullWidth
                                    label={t('series_booking_details.first_date')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled
                                    value={lastBooking ? formatDateDisplay(lastBooking.endDate) : ''}
                                    fullWidth
                                    label={t('series_booking_details.last_date')}
                                />
                            </Grid>
                            {firstBookingDetails?.checkIn && (
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled
                                        value={dayjs(firstBookingDetails.checkIn).format('HH:mm')}
                                        fullWidth
                                        label={t('create_booking.reservation_details.start_time')}
                                    />
                                </Grid>
                            )}
                            {firstBookingDetails?.checkOut && (
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled
                                        value={dayjs(firstBookingDetails.checkOut).format('HH:mm')}
                                        fullWidth
                                        label={t('create_booking.reservation_details.end_time')}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled
                                    value={firstBookingDetails?.city ?? ''}
                                    fullWidth
                                    label={t('create_booking.reservation_details.city')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'number'}
                                    onChange={() => {}}
                                    disabled
                                    value={firstBookingDetails?.numberOfGuests ?? 0}
                                    fullWidth
                                    label={t('create_booking.reservation_details.number_of_guests')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled
                                    value={
                                        firstBooking?.langNames?.[lang]?.title
                                        ?? firstBookingDetails?.apartment?.name
                                        ?? firstBooking?.assetName
                                        ?? ''
                                    }
                                    fullWidth
                                    label={
                                        t(`create_booking.reservation_details.${roomNounOf(firstBookingDetails?.useCase)}`)
                                    }
                                />
                            </Grid>
                            {firstBookingDetails?.eventType && (
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled
                                        value={firstBookingDetails.eventType}
                                        fullWidth
                                        label={t('create_booking.reservation_details.event_type')}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'number'}
                                    onChange={() => {}}
                                    disabled
                                    value={seriesBookings.length}
                                    fullWidth
                                    label={t('series_booking_details.occurrences')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth disabled={!isEditing}>
                                    <InputLabel id={'series-payment-frequency-label'}>
                                        {t('series_booking_details.payment_frequency.label')}
                                    </InputLabel>
                                    <Select
                                        labelId={'series-payment-frequency-label'}
                                        value={paymentTerm}
                                        label={t('series_booking_details.payment_frequency.label')}
                                        onChange={(e) => setPaymentTerm(e.target.value as SeriesPaymentTermSelection)}
                                    >
                                        {SERIES_PAYMENT_TERM_SELECTIONS.map((selection) => (
                                            <MenuItem key={selection} value={selection}>
                                                {t(`series_booking_details.payment_frequency.${selection}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Add-ons section (Phase 2, #771) — positioned before customer details
                        to match the single-booking edit page layout. */}
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-2'}>
                        {t('create_booking.additional_services.title')}
                    </Typography>
                    {firstBookingDetails?.allAdditionalServices && firstBookingDetails.allAdditionalServices.length > 0 ? (
                        <BookingEditDetailsAdditionalServices
                            additionalServices={editableServices}
                            allAdditionalServices={firstBookingDetails.allAdditionalServices}
                            disabled={!isEditing}
                            hint={t('series_booking_details.additional_services.applied_to_each_occurrence')}
                            callbacks={{
                                onAddService: ({ serviceId, name, quantity, unitPrice }) => {
                                    setEditableServices((prev) => {
                                        if (prev.some((s) => s.id === serviceId)) return prev;
                                        return [...prev, { id: serviceId, serviceName: name, quantity: quantity || 1, unitPrice }];
                                    });
                                },
                                onEditService: ({ serviceId, name, quantity }) => {
                                    setEditableServices((prev) => prev.map((s) =>
                                        s.id === serviceId || s.serviceName === name
                                            ? { ...s, quantity }
                                            : s,
                                    ));
                                },
                                onRemoveService: (name) => {
                                    setEditableServices((prev) => prev.filter((s) => s.serviceName !== name));
                                },
                            }}
                        />
                    ) : editableServices.length > 0 ? (
                        // The booking-details endpoint doesn't always return the asset's
                        // full service catalog (the editor needs it), but the series still
                        // has add-ons — list them read-only so they stay visible instead of
                        // silently disappearing behind the "not available" message.
                        <ul className={'list-disc pl-5 text-sm text-foreground'}>
                            {editableServices.map((s) => (
                                <li key={s.id}>
                                    {s.serviceName}
                                    {typeof s.quantity === 'number' && s.quantity > 0 ? ` × ${s.quantity}` : ''}
                                    {typeof s.unitPrice === 'number' ? ` — ${formatCurrency(s.unitPrice, getCurrentLang())}` : ''}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                            {t('series_booking_details.additional_services.not_available')}
                        </Typography>
                    )}

                    {/* Customer Details */}
                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <Typography variant={'h3'} className={'mb-2'}>
                        {t('create_booking.customer_details.title')}
                    </Typography>
                    <Grid container spacing={2} className={'w-full xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                        <Grid item xs={12} sm={6}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.firstName}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, firstName: val })}
                                fullWidth
                                label={t('create_booking.customer_details.first_name')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.lastName}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, lastName: val })}
                                fullWidth
                                label={t('create_booking.customer_details.last_name')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.email}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, email: val })}
                                fullWidth
                                label={t('create_booking.customer_details.email')}
                                valid={!isEditing || isCustomerEmailValid}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <PhoneNumberComponent
                                enabled={isEditing}
                                initialValue={editableCustomer.phone}
                                onChange={(value: string) => setEditableCustomer({ ...editableCustomer, phone: value })}
                                required={false}
                                label={t('create_booking.customer_details.phone')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.company}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, company: val })}
                                fullWidth
                                label={t('create_booking.customer_details.company')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BookingAttributionFields
                                value={editableAttribution}
                                onChange={setEditableAttribution}
                                onOrganizationPicked={(prefill) => setEditableCustomer((current) => ({
                                    ...current,
                                    company: prefill.company,
                                    ...(prefill.address !== undefined ? { address: prefill.address } : {}),
                                    ...(prefill.zipCode !== undefined ? { zipCode: prefill.zipCode } : {}),
                                    ...(prefill.city !== undefined ? { city: prefill.city } : {}),
                                    ...(prefill.country !== undefined ? { country: prefill.country } : {}),
                                }))}
                                // A covered series keeps the organisation that
                                // pays for it; the server refuses a change.
                                organizationLocked={firstBookingDetails?.coveredByOrg === true}
                                disabled={!isEditing}
                                loadFailed={firstBookingDetails?.attributionStatus === 'failed'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.address}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, address: val })}
                                fullWidth
                                label={t('create_booking.customer_details.address')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.zipCode}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, zipCode: val })}
                                fullWidth
                                label={t('create_booking.customer_details.zip_code')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldComponent
                                type={'string'}
                                disabled={!isEditing}
                                value={editableCustomer.city}
                                onChange={(val: any) => setEditableCustomer({ ...editableCustomer, city: val })}
                                fullWidth
                                label={t('create_booking.customer_details.city')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <BookingCountryField
                                disabled={!isEditing}
                                value={editableCustomer.country ?? ''}
                                onChange={(val) => setEditableCustomer((prev) => ({ ...prev, country: val }))}
                                label={t('create_booking.customer_details.country')}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                    <div className={'mb-4 flex flex-wrap items-center justify-between gap-2'}>
                        <Typography variant={'h3'}>
                            {t('series_booking_details.individual_bookings')}
                        </Typography>
                        {/* Occurrence count is editable through these two actions
                            (#1376). Hidden while the series-level edit mode is on
                            so there is never an unsaved series edit racing a
                            create/cancel loop that refetches the list underneath it. */}
                        {!isEditing && hasEditBookingsPermission && (
                            <div className={'flex gap-2'}>
                                <CustomButton
                                    title={t('series_booking_details.extend.button')}
                                    onClick={() => setOccurrenceAction(
                                        occurrenceAction === 'extend' ? 'none' : 'extend',
                                    )}
                                    isFilled={false}
                                />
                                <CustomButton
                                    title={t('series_booking_details.shorten.button')}
                                    onClick={() => setOccurrenceAction(
                                        occurrenceAction === 'shorten' ? 'none' : 'shorten',
                                    )}
                                    isFilled={false}
                                />
                            </div>
                        )}
                    </div>

                    {occurrenceAction === 'extend' && extendSource && (
                        <SeriesExtendPanel
                            seriesId={seriesId}
                            groupId={extendSource.groupId}
                            lastOccurrenceDate={extendSource.lastOccurrenceDate}
                            startTime={extendSource.startTime}
                            endTime={extendSource.endTime}
                            numberOfGuests={extendSource.numberOfGuests}
                            eventType={extendSource.eventType}
                            comment={extendSource.comment}
                            customerDetails={editableCustomer}
                            // #1810: the SAVED attribution, like the customer
                            // block; only when it was actually read.
                            attribution={attributionIfRead(
                                firstBookingDetails?.attributionStatus,
                                initialAttribution,
                            )}
                            additionalServices={editableServices.map((svc) => ({
                                serviceId: svc.id,
                                name: svc.serviceName,
                                quantity: svc.quantity ?? 1,
                            }))}
                            canContinue={seriesDetails?.canContinue === true}
                            onClose={() => setOccurrenceAction('none')}
                            onExtended={handleExtended}
                        />
                    )}

                    {occurrenceAction === 'shorten' && (
                        <SeriesShortenPanel
                            occurrences={seriesBookings}
                            onClose={() => setOccurrenceAction('none')}
                            seriesId={seriesId ?? ''}
                            onShortened={handleShortened}
                        />
                    )}

                    <div className={'mt-4 max-h-[500px] overflow-y-auto rounded-lg border border-gray-200'}>
                        {seriesBookings.map((booking, index) => (
                            <div
                                key={booking.bookingId}
                                className={`flex items-center justify-between border-b border-gray-100 px-4 py-3 last:border-b-0 ${
                                    booking.bookingStatus.toLowerCase() === 'canceled' ? 'bg-gray-50 opacity-60' : ''
                                }`}
                            >
                                <div className={'flex items-center gap-4'}>
                                    <span className={'min-w-[24px] text-sm font-medium text-gray-400'}>
                                        {index + 1}.
                                    </span>
                                    <span className={'text-sm font-medium'}>
                                        {dayjs(booking.startDate).locale(lang).format(
                                            lang === 'de' ? 'dd, DD.MM.YYYY' : 'ddd, MM/DD/YYYY',
                                        )}
                                    </span>
                                    <span className={'text-sm text-gray-500'}>
                                        {dayjs(booking.startDate).format('HH:mm')}&ndash;{dayjs(booking.endDate).format('HH:mm')}
                                    </span>
                                    <span className={'text-xs text-gray-400'}>
                                        {booking.bookingNumber}
                                    </span>
                                </div>
                                <div className={'flex items-center gap-3'}>
                                    <StatusBadge
                                        type={booking.bookingStatus.toLowerCase() as BookingStatusType}
                                        isEditing={false}
                                    />
                                    <StatusBadge
                                        type={paymentBadgeStatus(
                                            detailsById.get(booking.bookingId),
                                            getPaymentStatusLabel(booking.paymentStatus) as PaymentStatusType,
                                        )}
                                        isEditing={false}
                                    />
                                    <button
                                        onClick={() => navigate(makeRentalBookingDetailsUrl(booking.bookingId))}
                                        className={'text-sm text-gray-600 underline hover:text-black'}
                                    >
                                        {t('bookings.bookings_table.action')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {isEditing && (
                        <div className={'mt-6 flex space-x-2'}>
                            <CustomButton
                                title={t('series_booking_details.discard')}
                                onClick={handleDiscardEdit}
                                disabled={saveButtonsDisabled}
                                isFilled={false}
                            />
                            <CustomButton
                                title={t('edit_booking.button.confirm_only')}
                                onClick={() => handleSaveSeries('save_only')}
                                loading={loadingSaveOnly}
                                disabled={saveButtonsDisabled}
                                isFilled={false}
                            />
                            <CustomButton
                                title={t('edit_booking.button.confirm_and_send')}
                                onClick={() => setConfirmSendOpen(true)}
                                disabled={saveButtonsDisabled || eligibleBookingCount === 0}
                                isFilled={true}
                            />
                        </div>
                    )}
                </Paper>
            </Box>
            <CustomModal
                open={confirmSendOpen}
                onClose={() => setConfirmSendOpen(false)}
                title={t('series_booking_details.send_modal.title')}
                description={t('series_booking_details.send_modal.description', {
                    eligible: eligibleBookingCount,
                    total: seriesBookings.length,
                })}
                saveButton={
                    <CustomButton
                        title={t('series_booking_details.send_modal.confirm_button')}
                        onClick={() => handleSaveSeries('save_and_send_email')}
                        disabled={saveButtonsDisabled}
                        loading={loadingSaveAndSend}
                        isFilled={true}
                        fullWidth={false}
                    />
                }
                closeButton={
                    <CustomButton
                        title={t('series_booking_details.send_modal.cancel_button')}
                        onClick={() => setConfirmSendOpen(false)}
                        isFilled={false}
                    />
                }
            />
            <ConfirmationDialog
                isLoading={loadingGlobal || isCancelingSeries}
                handleClickConfirm={
                    dialogAction === 'delete'
                        ? handleDeleteSeries
                        : () => handleCancelSeries('save_and_send_email')
                }
                handleClickMiddle={
                    dialogAction === 'delete' ? undefined : () => handleCancelSeries('save_only')
                }
            />
            <SnackbarComponent
                open={snackbarData.open}
                message={snackbarData.message}
                severity={snackbarData.severity}
                onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
            />
        </Container>
    );
};

export default SeriesBookingDetailsPage;
